/**
 * Intake: pick the bin once, then scan.
 *
 * The whole point of chaos sorting is that this screen asks *one* question - which
 * bin is open - and then never asks where a card goes again. So the bin is chosen
 * before the first scan, it is shown while scanning, and every card in the batch
 * lands in it. Changing your mind means closing the session and opening another,
 * which is a deliberate act rather than a per-card decision.
 *
 * The session is server state (`POST /api/intake-sessions`), not local: the slot
 * numbers, the copy count and the close are all the API's, and a phone that
 * reloads mid-batch can read the same session back.
 */

import { useEffect, useState } from 'react';
import { ApiError, type Api } from '../api/http.js';
import type { Container, IntakeSession, Meta } from '../api/types.js';
import { Banner } from '../components/Banner.js';
import type { ArtIndex } from '@unsorted/shared';
import type { CaptureSource } from '../scan/capture.js';
import type { ChosenPrinting } from '../scan/queue.js';
import { loadSettings } from '../settings.js';
import type { Async } from '../state/useAsync.js';
import { findEntry, walkOrder } from '../storage/walk.js';
import { ScanSurface } from './ScanSurface.js';

export interface IntakeScreenProps {
  api: Api;
  meta: Meta | null;
  /** Owned by the shell: a bin created on the Bins tab is a bin to scan into now. */
  containers: Async<Container[]>;
  reloadContainers: () => void;
  capture?: CaptureSource;
  /**
   * The art index installed on this device, or nothing where there is none.
   *
   * Passed down rather than imported, so a test can hand the surface an index and the
   * scan path can be driven with the server unreachable.
   */
  localArtIndex?: () => Promise<ArtIndex | null>;
  onFiled: (printings: ChosenPrinting[]) => void;
}

/**
 * Which session this device has open.
 *
 * A phone reloads mid-batch - a call arrives, the browser drops the tab, the page
 * is refreshed to see something - and the session belongs to the server, not to
 * this page's memory. Remembering the id turns a reload from "where was I? open a
 * new session, and leave the old one open forever" into the same batch, still
 * filing into the same bin. Scoped by nothing but the browser profile, because the
 * cookie already scopes it to one account.
 */
const SESSION_KEY = 'unsorted.open-session';

const rememberSession = (publicId: string | null): void => {
  try {
    if (publicId === null) window.localStorage.removeItem(SESSION_KEY);
    else window.localStorage.setItem(SESSION_KEY, publicId);
  } catch {
    // Private mode, or storage refused. Losing the memory is not worth failing a
    // scan over: the session is still open on the server.
  }
};

const rememberedSession = (): string | null => {
  try {
    return window.localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
};

export function IntakeScreen({
  api,
  meta,
  containers,
  reloadContainers,
  capture,
  localArtIndex,
  onFiled,
}: IntakeScreenProps) {
  const [session, setSession] = useState<IntakeSession | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [binPublicId, setBinPublicId] = useState('');
  const [label, setLabel] = useState('');
  // The Settings screen's default prefills this, without owning it: a stored
  // value the server no longer offers falls back to auto.
  const [modeOverride, setModeOverride] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pick the batch back up where the last one left off, if it is still open.
  useEffect(() => {
    const remembered = rememberedSession();
    if (remembered === null) {
      setRestoring(false);
      return;
    }
    let cancelled = false;
    api
      .intakeSession(remembered)
      .then((found) => {
        if (cancelled) return;
        if (found.status === 'open') setSession(found);
        else rememberSession(null);
      })
      .catch(() => {
        // Gone, or never belonged to this account: forget it and start clean.
        if (!cancelled) rememberSession(null);
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const containersValue = containers.status === 'ready' ? containers.value : [];
  const modes = meta?.vocabularies.recognitionModes ?? ['auto', 'fast', 'visual', 'manual'];
  const storedMode = loadSettings().recognitionMode;
  const mode = modeOverride ?? (storedMode !== null && modes.includes(storedMode) ? storedMode : 'auto');
  const entries = walkOrder(containersValue);
  const openEntry = session?.targetBinPublicId ? findEntry(containersValue, session.targetBinPublicId) : null;

  const open = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const opened = await api.openIntakeSession({
        targetBinPublicId: binPublicId,
        ...(label === '' ? {} : { label }),
        recognitionMode: mode,
      });
      setSession(opened);
      rememberSession(opened.publicId);
      setLabel('');
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'the session could not be opened');
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const closed = await api.closeIntakeSession(session.publicId);
      setSession(closed);
      rememberSession(null);
      reloadContainers();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'the session could not be closed');
    } finally {
      setBusy(false);
    }
  };

  if (containers.status === 'error') {
    return (
      <div className="stack">
        <h2>Scan</h2>
        <Banner kind="error" onRetry={reloadContainers}>
          {containers.message}
        </Banner>
      </div>
    );
  }

  if (containers.status === 'ready' && entries.length === 0) {
    return (
      <div className="stack">
        <h2>Scan</h2>
        <div className="card">
          <h3>A bin has to exist before a card can be filed</h3>
          <p className="hint">
            Scanning files cards into whichever bin is open, so there has to be one. Add a container and a bin on the
            Bins tab, then come back.
          </p>
        </div>
      </div>
    );
  }

  if (restoring && session === null) {
    return (
      <div className="stack">
        <h2>Scan</h2>
        <p role="status" className="hint" aria-live="polite">
          Looking for the batch you had open…
        </p>
        <div className="card" aria-busy="true" aria-hidden="true">
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  if (session === null || session.status === 'closed') {
    return (
      <div className="stack single">
        <h2>Scan</h2>
        <p className="hint">
          Choose the bin this batch goes into. Every card you scan lands in it, at the next free slot - you do not
          decide anything per card.
        </p>

        {session?.status === 'closed' && (
          <Banner kind="success">
            That session is closed with {session.copiesAdded} card{session.copiesAdded === 1 ? '' : 's'} filed.
          </Banner>
        )}
        {error && <Banner kind="error">{error}</Banner>}

        <form className="card" onSubmit={(event) => void open(event)}>
          <label>
            Bin
            <select
              name="bin"
              value={binPublicId}
              onChange={(event) => setBinPublicId(event.target.value)}
              required
              disabled={entries.length === 0}
            >
              <option value="">Choose a bin…</option>
              {entries.map((entry) => (
                <option key={entry.bin.publicId} value={entry.bin.publicId}>
                  {entry.address} · {entry.bin.copyCount} card{entry.bin.copyCount === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          </label>

          <label>
            Batch label (optional)
            <input
              name="label"
              placeholder="Opening night…"
              autoComplete="off"
              spellCheck={false}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              maxLength={120}
            />
          </label>

          <label>
            Recognition
            <select name="mode" value={mode} onChange={(event) => setModeOverride(event.target.value)}>
              {modes.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <p className="hint">
            The bin order above is the walk order: container by container, bins inside each. A picklist is built in the
            same order, so filing into the next bin you walk past is what makes pulling cheap.
          </p>

          <div className="form-foot">
            <button type="submit" className="primary" disabled={busy || binPublicId === ''}>
              {busy ? 'Opening…' : 'Open this bin for scanning'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="stack">
      <header className="row session-head">
        <div>
          <h2>{session.label ?? 'Scanning'}</h2>
          <p className="hint" aria-live="polite">
            Filing into <strong className="bin-name">{openEntry ? openEntry.address : 'the open bin'}</strong> ·{' '}
            <span className="tnum">
              {session.copiesAdded} card{session.copiesAdded === 1 ? '' : 's'} filed
            </span>{' '}
            · {session.recognitionMode} recognition
          </p>
        </div>
        <button type="button" className="ghost" onClick={() => void close()} disabled={busy}>
          Close session
        </button>
      </header>

      {error && <Banner kind="error">{error}</Banner>}

      <ScanSurface
        api={api}
        session={session}
        meta={meta}
        {...(capture ? { capture } : {})}
        {...(localArtIndex ? { localArtIndex } : {})}
        onSession={setSession}
        onFiled={(printings) => {
          onFiled(printings);
          reloadContainers();
        }}
        targetAddress={openEntry ? openEntry.address : 'the open bin'}
      />
    </div>
  );
}
